import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PartyPopper } from "lucide-react";

interface CompletionCelebrationProps {
  points: number;
  show: boolean;
  onDone?: () => void;
}

export function CompletionCelebration({
  points,
  show,
  onDone,
}: CompletionCelebrationProps) {
  useEffect(() => {
    if (show) {
      // Subtle haptic feedback on supported devices
      navigator.vibrate?.(10);
      const timer = setTimeout(() => onDone?.(), 1000);
      return () => clearTimeout(timer);
    }
  }, [show, onDone]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 1, y: 0, scale: 1 }}
          animate={{ opacity: 0, y: -40, scale: 1.1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="absolute top-0 right-4 pointer-events-none z-10"
          role="status"
          aria-live="polite"
        >
          <span className="text-sm font-extrabold text-white bg-emerald-500 shadow-soft rounded-full px-3 py-1">
            +{points} pts
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

interface AllDoneCelebrationProps {
  show: boolean;
  streakCount: number;
}

export function AllDoneCelebration({
  show,
  streakCount,
}: AllDoneCelebrationProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show) {
      setVisible(true);
      navigator.vibrate?.(20);
      const timer = setTimeout(() => setVisible(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [show]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 pointer-events-none"
          role="status"
          aria-live="polite"
        >
          <div className="bg-card rounded-3xl p-8 text-center shadow-sheet max-w-xs mx-4 border border-border">
            <motion.div
              initial={{ scale: 0.5, rotate: -10 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 400, damping: 15, delay: 0.1 }}
              className="mx-auto mb-4 w-16 h-16 rounded-3xl bg-gradient-to-br from-sky-500 to-violet-500 text-white flex items-center justify-center shadow-glow-cyan"
              aria-hidden="true"
            >
              <PartyPopper className="w-8 h-8" />
            </motion.div>
            <div className="text-3xl font-extrabold tracking-tight text-foreground mb-1">
              Day {streakCount}
            </div>
            <div className="text-foreground text-lg font-bold mb-1">
              Complete!
            </div>
            <div className="text-muted-foreground text-sm">
              All tasks done. Great work!
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
